import {
  applicantSection,
  feeProofAttachment,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * G9. Wniosek o wydanie karty wędkarskiej — art. 7 ust. 2, 5 i 6 ustawy z 18.04.1985 r. o rybactwie śródlądowym (karta od
 * 14 lat, egzamin przed komisją organizacji społecznej, zwolnienie z egzaminu przy wykształceniu z zakresu rybactwa);
 * § 5 rozporządzenia MRiRW z 12.07.2023 r. (Dz.U. 2023 poz. 1373) — opłata 10 zł, wzór karty w załączniku nr 1 (imię
 * i nazwisko, data urodzenia, fotografia) — oba akty sprawdzone w tekście 22.10.2026. Brak wzoru wniosku w rozporządzeniu;
 * układ jak wzór Starostwa Powiatowego we Włocławku
 * (https://bip.wloclawski.pl/download/attachment/15993/wniosek-karty-wedkarskie.pdf?v=1767955180, pobrany 22.10.2026):
 * dane wnioskodawcy, załączniki (zaświadczenie o egzaminie, zdjęcie, dowód opłaty), odbiór karty. Datę urodzenia (na
 * karcie) urząd odczyta z numeru PESEL; pominięto miejsce urodzenia i przynależność do koła (nie ma ich na karcie ani
 * w przepisach). Wtórnik z oświadczeniem o utracie — praktyka
 * urzędów (do potwierdzenia z wydziałem). Rejestracja łodzi wędkarskiej odbywa się w REJA24 — w formularzu tylko informacja.
 */
const FIRST: EbiCondition = { field: "rodzajWniosku", equals: "pierwsza" };
const DUPLICATE: EbiCondition = { field: "rodzajWniosku", equals: "wtornik" };
const EXAM: EbiCondition = { all: [FIRST, { field: "podstawaWydania", equals: "egzamin" }] };
const EDUCATION: EbiCondition = { all: [FIRST, { field: "podstawaWydania", equals: "wyksztalcenie" }] };

const G9: IEbiFormDefinition = {
  code: "G9",
  version: 1,
  title: "Wniosek o wydanie karty wędkarskiej",
  category: EEbiFormCategory.Permits,
  office: EEbiFormOffice.County,
  description: "Karta wędkarska po zdanym egzaminie (od 14 lat). Rejestracja łodzi wędkarskiej — w systemie REJA24.",
  keywords: ["karta wędkarska", "wędkarstwo", "połów ryb", "egzamin", "PZW", "łódź", "REJA24", "sprzęt pływający"],
  organ: "Starosta właściwy ze względu na miejsce zamieszkania",
  legalBasis:
    "art. 7 ust. 5 ustawy z dnia 18 kwietnia 1985 r. o rybactwie śródlądowym; § 5 rozporządzenia Ministra Rolnictwa i Rozwoju Wsi z dnia 12 lipca 2023 r. w sprawie szczegółowych warunków ochrony i połowu ryb w powierzchniowych wodach śródlądowych",
  request:
    "Wnoszę o wydanie karty wędkarskiej uprawniającej do amatorskiego połowu ryb — zgodnie z ustawą z dnia 18 kwietnia 1985 r. o rybactwie śródlądowym.",
  feeLabel: "10 zł",
  fee: "10 zł za wydanie karty wędkarskiej (bez opłaty skarbowej); pełnomocnictwo — 17 zł.",
  sections: [
    {
      code: "reja24",
      title: "Rejestracja łodzi wędkarskiej",
      fields: [
        {
          code: "infoReja24",
          type: "info",
          label:
            "Sprzęt pływający służący do amatorskiego połowu ryb rejestruje się w rejestrze jachtów i innych jednostek pływających o długości do 24 m (REJA24), a nie tym wnioskiem. Rejestracja jest obowiązkowa dla jednostek o długości powyżej 7,5 m lub z napędem o mocy powyżej 15 kW, mniejsze można zarejestrować dobrowolnie. Wniosek złożysz przez Konto Interesanta REJA24 — https://interesant.reja24.gov.pl/ (informacje: https://portal.reja24.gov.pl/hub) — albo w wybranym organie rejestrującym (starosta, Polski Związek Żeglarski, Polski Związek Motorowodny i Narciarstwa Wodnego); opłata za rejestrację wynosi 80 zł.",
        },
      ],
    },
    applicantSection(),
    representationSection(),
    {
      code: "karta",
      title: "Karta wędkarska",
      description: "Kartę wędkarską wydaje się osobie, która ukończyła 14 lat (art. 7 ust. 6 ustawy).",
      fields: [
        {
          code: "rodzajWniosku",
          type: "radio",
          label: "Wnoszę o",
          required: true,
          defaultValue: "pierwsza",
          options: [
            { value: "pierwsza", label: "wydanie karty wędkarskiej" },
            { value: "wtornik", label: "wydanie nowej karty w miejsce utraconej lub zniszczonej" },
          ],
        },
        {
          code: "podstawaWydania",
          type: "radio",
          label: "Podstawa wydania karty",
          required: true,
          visibleWhen: FIRST,
          defaultValue: "egzamin",
          options: [
            { value: "egzamin", label: "zdany egzamin ze znajomości zasad i warunków ochrony i połowu ryb przed komisją egzaminacyjną" },
            {
              value: "wyksztalcenie",
              label: "średnie, średnie branżowe lub wyższe wykształcenie z zakresu rybactwa — zwolnienie z egzaminu (art. 7 ust. 5 ustawy)",
            },
          ],
        },
        { code: "dotychczasowaKartaNumer", type: "text", label: "Numer utraconej karty (jeśli znany)", visibleWhen: DUPLICATE, width: "half", maxLength: 50 },
        {
          code: "dotychczasowaKartaOrgan",
          type: "text",
          label: "Starostwo, które wydało utraconą kartę",
          required: true,
          visibleWhen: DUPLICATE,
          width: "half",
          maxLength: 200,
        },
        {
          code: "oswiadczenieUtrata",
          type: "declaration",
          label: "Oświadczam, że dotychczasowa karta wędkarska została utracona albo zniszczona.",
          required: true,
          visibleWhen: DUPLICATE,
        },
        {
          code: "odbiorKarty",
          type: "radio",
          label: "Odbiór karty",
          required: true,
          options: [
            { value: "poczta", label: "pocztą na adres do korespondencji" },
            { value: "osobiscie", label: "osobiście w starostwie" },
          ],
        },
        { code: "infoOplata", type: "info", label: "Opłata za wydanie karty wędkarskiej wynosi 10 zł (§ 5 rozporządzenia) — dołącz potwierdzenie wpłaty na rachunek starostwa." },
      ],
    },
    replySection("Sposób odbioru korespondencji w sprawie"),
  ],
  attachments: [
    {
      code: "zaswiadczenieEgzamin",
      label: "Zaświadczenie o zdanym egzaminie ze znajomości ochrony i połowu ryb",
      hint: "Wydane przez komisję egzaminacyjną organizacji społecznej (np. Polskiego Związku Wędkarskiego).",
      visibleWhen: EXAM,
      required: true,
    },
    {
      code: "dyplomRybacki",
      label: "Dyplom lub świadectwo potwierdzające wykształcenie z zakresu rybactwa",
      visibleWhen: EDUCATION,
      required: true,
    },
    {
      code: "fotografia",
      label: "Aktualna fotografia",
      hint: "Zdjęcie legitymacyjne (JPG lub PNG) — zostanie umieszczone na karcie.",
      required: true,
    },
    feeProofAttachment("Dowód wniesienia opłaty za wydanie karty wędkarskiej (10 zł)"),
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default G9;
